/*
 * IndianMarketPage — welcome + TradingView onboarding + TRADER Mindset.
 *
 * Rendered when the user clicks the "Indian Market" tab in the affiliate
 * dashboard header. It's the dedicated onboarding surface — moved out of
 * HomeHero to keep the landing view clean.
 *
 * Content (per product spec):
 *   1. Welcome intro paragraph
 *   2. Step-by-step TradingView setup (Veloxalgotech HTF)
 *   3. Recommended indicator settings table (updated defaults)
 *   4. THE ANATOMY OF A MATRIX — TRADER Mindset
 */

import { useEffect, useState } from "react";
import {
  ChartLine, PlayCircle, MagnifyingGlass, Sliders, Gear, Sparkle, Robot,
  Clock, Info,
} from "@phosphor-icons/react";

// ==================== SETTINGS (per new spec) ====================
const INDICATOR_SETTINGS = [
  { category: "EMA Settings",         rows: [
    { param: "Fast EMA",        value: "9"  },
    { param: "Slow EMA",        value: "21" },
  ]},
  { category: "Velox ATR Settings",   rows: [
    { param: "ATR Period",      value: "5"   },
    { param: "ATR Multiplier",  value: "3.5" },
  ]},
  { category: "Buy Settings (Long)",  rows: [
    { param: "Min Buy Angle",   value: "45°" },
    { param: "Risk : Reward",   value: "2"   },
    { param: "Buy SL Buffer",   value: "25"  },
  ]},
  { category: "Sell Settings (Short)", rows: [
    { param: "Max Sell Angle",  value: "45°" },
    { param: "Risk : Reward",   value: "2"   },
    { param: "Sell SL Buffer",  value: "25"  },
  ]},
  { category: "Filters",              rows: [
    { param: "Marubozu Body % Threshold", value: "90" },
  ]},
];

const TRADER_ROWS = [
  { letter: "T", title: "Time",       quote: "Your time is limited, so don't waste it living someone else's life.",           author: "Steve Jobs" },
  { letter: "R", title: "Risk",       quote: "The biggest risk in life is not taking any risk.",                              author: "Mark Zuckerberg" },
  { letter: "A", title: "Analysis",   quote: "Price is what you pay. Value is what you get.",                                 author: "Warren Buffett" },
  { letter: "D", title: "Discipline", quote: "The big money is not in the buying and selling, but in the waiting.",           author: "Charlie Munger" },
  { letter: "E", title: "Emotion",    quote: "If investing is entertaining for you, if it's fun, you're probably not making any money.", author: "George Soros" },
  { letter: "R", title: "Rewards",    quote: "Pain + Reflection = Progress.",                                                 author: "Ray Dalio" },
];

// ==================== MAIN ====================
export default function IndianMarketPage() {
  return (
    <div className="space-y-8" data-testid="indian-market-page">
      {/* ---------- Welcome hero ---------- */}
      <section
        className="relative overflow-hidden rounded-2xl px-6 py-10 md:px-12 md:py-14"
        style={{
          background: "linear-gradient(180deg, var(--velox-panel) 0%, var(--velox-bg-elevated) 100%)",
          border: "1px solid var(--velox-border-strong)",
        }}
        data-testid="im-welcome"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full bg-[#00b4d8]/8 blur-[130px]" />
          <div className="absolute -bottom-32 -right-24 w-[500px] h-[500px] rounded-full bg-[#0077b6]/10 blur-[140px]" />
        </div>

        <div className="relative z-10 max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4"
               style={{ background: "rgba(0,180,216,0.1)", border: "1px solid rgba(0,180,216,0.3)" }}>
            <Sparkle size={11} weight="fill" style={{ color: "#00b4d8" }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: "#00b4d8" }}>
              Indian Market · AI-Powered
            </span>
          </div>

          <h1
            className="text-3xl md:text-5xl font-black tracking-tight text-white mb-4 leading-[1.05]"
            style={{ fontFamily: "'Outfit', 'Manrope', sans-serif" }}
            data-testid="im-title"
          >
            Welcome to <span style={{ color: "#00b4d8" }}>Veloxalgotech</span>
          </h1>
          <p className="text-sm md:text-base text-zinc-300 leading-relaxed max-w-3xl">
            <span className="font-semibold text-white">An AI-powered automation tool.</span>{" "}
            You can explore our TradingView indicator and test it until you are comfortable.
            Once you believe in the power of our AI-driven automation tool, you can start
            using it fully. It delivers up to <span className="font-bold" style={{ color: "#06d6a0" }}>99% trading accuracy</span>.
          </p>

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md"
                 style={{ background: "var(--velox-panel-2)", border: "1px solid var(--velox-border)" }}>
              <Robot size={14} weight="duotone" style={{ color: "#00b4d8" }} />
              <span className="text-xs text-zinc-300 font-medium">Indicator:</span>
              <span className="text-xs font-bold text-white font-mono">Veloxalgotech HTF</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md"
                 style={{ background: "rgba(6,214,160,0.1)", border: "1px solid rgba(6,214,160,0.3)" }}>
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#06d6a0" }}>
                Up to 99% Accuracy
              </span>
            </div>
          </div>

          {/* ---------- Notice banners ---------- */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="im-notices">
            {/* Notice 1 — Automated Trading coming soon */}
            <div
              data-testid="im-notice-automation"
              className="flex items-start gap-3 p-4 rounded-xl"
              style={{
                background: "linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(245,158,11,0.02) 100%)",
                border: "1px solid rgba(245,158,11,0.25)",
              }}
            >
              <span
                className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center"
                style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.4)" }}
              >
                <Clock size={15} weight="duotone" style={{ color: "#F59E0B" }} />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: "#F59E0B" }}>
                    Coming Soon
                  </span>
                  <span
                    className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider"
                    style={{ background: "#F59E0B", color: "#0b132b" }}
                  >
                    NEW
                  </span>
                </div>
                <p className="text-xs font-bold text-white leading-snug">
                  Automated Trading for Indian Market will be available soon
                </p>
                <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
                  Until then, use the indicator manually on your charts — we&apos;ll notify you the moment full automation goes live.
                </p>
              </div>
            </div>

            {/* Notice 2 — Banknifty only */}
            <div
              data-testid="im-notice-banknifty"
              className="flex items-start gap-3 p-4 rounded-xl"
              style={{
                background: "linear-gradient(135deg, rgba(0,180,216,0.1) 0%, rgba(0,180,216,0.02) 100%)",
                border: "1px solid rgba(0,180,216,0.3)",
              }}
            >
              <span
                className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center"
                style={{ background: "rgba(0,180,216,0.15)", border: "1px solid rgba(0,180,216,0.4)" }}
              >
                <Info size={15} weight="duotone" style={{ color: "#00b4d8" }} />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: "#00b4d8" }}>
                    Important
                  </span>
                </div>
                <p className="text-xs font-bold text-white leading-snug">
                  These indicator settings are tuned for <span style={{ color: "#00b4d8" }}>Banknifty only</span>
                </p>
                <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
                  Applying them to other instruments (Nifty, stocks, forex) will produce unreliable signals — separate presets per symbol will roll out with the automated engine.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- TradingView onboarding ---------- */}
      <TradingViewOnboarding />

      {/* ---------- TRADER Mindset ---------- */}
      <TraderMindset />
    </div>
  );
}

// ==================== TRADINGVIEW ONBOARDING ====================
function TradingViewOnboarding() {
  const steps = [
    { icon: PlayCircle,     title: "Open your chart on TradingView",                    body: "Log in to your TradingView account and open any symbol you plan to trade — e.g. NIFTY, BANKNIFTY, or your favourite index/stock." },
    { icon: MagnifyingGlass, title: 'Search for "Veloxalgotech HTF"',                   body: "Click the fx / Indicators button, search Veloxalgotech HTF and add our published indicator to your chart." },
    { icon: Sliders,        title: "Adjust indicator settings",                         body: "Open the indicator settings menu (gear icon) and update the values to match the recommended defaults below." },
    { icon: Gear,           title: "You're ready — begin paper-testing",                body: "Watch signals form on live charts. Get comfortable with the entries and exits before you go live." },
  ];

  return (
    <section data-testid="im-tv-onboarding">
      <div className="flex items-center gap-2 mb-4">
        <span
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: "rgba(0,180,216,0.12)", border: "1px solid rgba(0,180,216,0.3)" }}
        >
          <PlayCircle size={16} weight="duotone" style={{ color: "#00b4d8" }} />
        </span>
        <div>
          <h2 className="text-sm font-bold text-white tracking-wide">Getting Started · TradingView Setup</h2>
          <p className="text-[11px] text-zinc-400">4 steps to install and configure the Veloxalgotech HTF indicator.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={i}
              data-testid={`im-tv-step-${i + 1}`}
              className="relative p-4 rounded-xl transition-colors"
              style={{ background: "var(--velox-panel)", border: "1px solid var(--velox-border)" }}
            >
              <div className="absolute top-3 right-3 text-[10px] font-black text-zinc-700 font-mono">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center"
                  style={{ background: "rgba(0,180,216,0.1)", border: "1px solid rgba(0,180,216,0.25)" }}
                >
                  <Icon size={16} weight="duotone" style={{ color: "#00b4d8" }} />
                </div>
                <div className="min-w-0 pr-6">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: "#00b4d8" }}>
                      Step {i + 1}
                    </span>
                  </div>
                  <h3 className="text-xs font-bold text-white leading-snug mb-1">{s.title}</h3>
                  <p className="text-[11px] text-zinc-400 leading-relaxed">{s.body}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <IndicatorSettingsTable />
    </section>
  );
}

function IndicatorSettingsTable() {
  return (
    <div
      data-testid="im-tv-settings-table"
      className="rounded-xl overflow-hidden"
      style={{
        background: "linear-gradient(135deg, var(--velox-panel) 0%, var(--velox-bg-elevated) 100%)",
        border: "1px solid var(--velox-border)",
      }}
    >
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--velox-border)" }}>
        <div className="flex items-center gap-2">
          <Gear size={14} weight="duotone" style={{ color: "#00b4d8" }} />
          <h3 className="text-xs font-bold text-white">Recommended Indicator Settings</h3>
        </div>
        <span className="text-[10px] uppercase tracking-wider" style={{ color: "#06d6a0" }}>Master Strategy Defaults</span>
      </div>

      <div className="hidden md:grid grid-cols-[1fr_1.2fr_120px] gap-4 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500"
           style={{ background: "rgba(255,255,255,0.02)" }}>
        <span>Category</span>
        <span>Parameter</span>
        <span className="text-right">Recommended Value</span>
      </div>

      <div>
        {INDICATOR_SETTINGS.map((section, sIdx) => (
          <div key={sIdx} style={{ borderTop: sIdx > 0 ? "1px solid var(--velox-border)" : "none" }}>
            {section.rows.map((row, rIdx) => (
              <div
                key={rIdx}
                className="grid grid-cols-[1fr_1.2fr_120px] gap-4 px-4 py-3 items-center transition-colors hover:bg-white/[0.02]"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}
                data-testid={`im-setting-${row.param.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <span className={`text-[11px] ${rIdx === 0 ? "text-zinc-200 font-semibold" : "text-zinc-500"}`}>
                  {rIdx === 0 ? section.category : ""}
                </span>
                <span className="text-xs text-zinc-300">{row.param}</span>
                <span className="text-sm font-black font-mono text-right" style={{ color: "#00b4d8" }}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="px-4 py-3" style={{ borderTop: "1px solid var(--velox-border)", background: "rgba(255,255,255,0.02)" }}>
        <p className="text-[10px] text-zinc-500 leading-relaxed">
          <span className="font-semibold" style={{ color: "#F59E0B" }}>Note:</span>{" "}
          These are the exact settings our master account trades with. Using different
          values will cause your entries to drift from copied signals. Restore defaults
          anytime from the indicator&apos;s settings menu → <span className="text-zinc-300">Defaults → Reset Settings</span>.
        </p>
      </div>
    </div>
  );
}

// ==================== TRADER MINDSET ====================
function TraderMindset() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setActiveIndex((i) => (i + 1) % TRADER_ROWS.length), 3000);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section
      data-testid="im-trader-mindset"
      className="relative rounded-2xl px-6 py-10 md:px-12 md:py-14 overflow-hidden"
      style={{ background: "var(--velox-panel)", border: "1px solid var(--velox-border)" }}
    >
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute top-0 left-1/4 w-[380px] h-[380px] rounded-full bg-[#d4af37]/8 blur-[130px]" />
        <div className="absolute bottom-0 right-1/4 w-[420px] h-[420px] rounded-full bg-amber-500/6 blur-[140px]" />
      </div>

      <div className="relative z-10 text-center mb-8">
        <p
          className="text-[11px] md:text-xs font-bold tracking-[0.35em] uppercase mb-2"
          style={{ color: "#d4af37" }}
          data-testid="im-trader-tagline"
        >
          The Anatomy of a Matrix
        </p>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto flex flex-col gap-4" data-testid="im-trader-grid">
        {TRADER_ROWS.map((row, idx) => {
          const isActive = idx === activeIndex && !paused;
          return (
            <div
              key={idx}
              data-testid={`im-trader-row-${idx}`}
              onMouseEnter={() => { setPaused(true); setActiveIndex(-1); }}
              onMouseLeave={() => { setPaused(false); }}
              className="im-trader-row flex items-start rounded-md p-5 cursor-pointer transition-all duration-500 border-l-[3px]"
              style={{
                background: isActive
                  ? "linear-gradient(90deg, rgba(212,175,55,0.05) 0%, rgba(255,255,255,0.02) 100%)"
                  : "linear-gradient(90deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)",
                borderLeftColor: isActive ? "#d4af37" : "transparent",
                transform: isActive ? "translateX(10px)" : "translateX(0)",
              }}
            >
              <div
                className="text-5xl md:text-6xl font-black leading-none w-14 md:w-20 text-left transition-colors duration-300"
                style={{
                  color: isActive ? "#d4af37" : "rgba(255,255,255,0.3)",
                  textShadow: isActive ? "0 0 20px rgba(212,175,55,0.35)" : "none",
                  fontFamily: "'Cabinet Grotesk','Inter',sans-serif",
                }}
              >
                {row.letter}
              </div>
              <div className="text-left ml-3 md:ml-5 flex flex-col justify-center">
                <div className="text-[13px] md:text-[15px] font-bold uppercase tracking-[0.1em] text-white mb-1">
                  {row.title}
                </div>
                <div className={`text-sm md:text-[15px] font-light italic leading-snug transition-colors duration-300 ${isActive ? "text-white" : "text-zinc-400"}`}>
                  &ldquo;{row.quote}&rdquo;
                </div>
                <div className="text-[11px] font-semibold uppercase tracking-wider mt-1.5" style={{ color: "#d4af37" }}>
                  — {row.author}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div
        className="relative z-10 text-center mt-10 text-base md:text-2xl font-bold uppercase tracking-[0.15em] text-white"
        data-testid="im-trader-conclusion"
      >
        TRADER: Not just a profession. It&apos;s a <span style={{ color: "#d4af37" }}>mindset.</span>
      </div>

      <style>{`
        .im-trader-row:hover {
          background: linear-gradient(90deg, rgba(212,175,55,0.05) 0%, rgba(255,255,255,0.02) 100%) !important;
          border-left-color: #d4af37 !important;
          transform: translateX(10px) !important;
        }
        .im-trader-row:hover > div:first-child {
          color: #d4af37 !important;
          text-shadow: 0 0 20px rgba(212,175,55,0.35);
        }
        .im-trader-row:hover .text-zinc-400 { color: #ffffff !important; }
      `}</style>
    </section>
  );
}
